import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { azn, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Wallet as WalletIcon, ArrowDownToLine, ArrowUpFromLine, Loader2, Landmark, CreditCard, Copy, Check } from "lucide-react";

const statusMap = {
  pending: { label: "Gözləyir", variant: "secondary" },
  approved: { label: "Təsdiqləndi", variant: "default" },
  rejected: { label: "Rədd edildi", variant: "destructive" },
  completed: { label: "Tamamlandı", variant: "default" },
};

const txLabel = {
  deposit: "Depozit", withdrawal: "Çıxarış", purchase: "Ağac alışı",
  income: "Gəlir", principal: "Əsas məbləğ", adjustment: "Düzəliş", refund: "Geri qaytarma", wheel: "Çarx qazancı",
};

export default function Wallet() {
  const { user, refresh } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [info, setInfo] = useState(null);
  const [txs, setTxs] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [depAmount, setDepAmount] = useState("");
  const [depNote, setDepNote] = useState("");
  const [wdAmount, setWdAmount] = useState("");
  const [wdDetails, setWdDetails] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const [w, t, d, wd, di] = await Promise.all([
        api.get("/wallet"), api.get("/transactions"), api.get("/deposits"), api.get("/withdrawals"), api.get("/deposit-info"),
      ]);
      setWallet(w.data); setTxs(t.data); setDeposits(d.data); setWithdrawals(wd.data); setInfo(di.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const minDep = info?.min_deposit ?? 30;
  const minWd = info?.min_withdrawal ?? 25;

  const copyCard = () => {
    const num = (info?.card_number || "").replace(/\s/g, "");
    navigator.clipboard?.writeText(num);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const submitDeposit = async (e) => {
    e.preventDefault();
    if (parseFloat(depAmount) < minDep) { toast.error(`Minimum depozit ${minDep} ₼-dır`); return; }
    setBusy(true);
    try {
      await api.post("/deposits", { amount: parseFloat(depAmount), note: depNote });
      toast.success("Depozit sorğusu göndərildi. Admin təsdiqini gözləyin.");
      setDepAmount(""); setDepNote("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const submitWithdraw = async (e) => {
    e.preventDefault();
    if (parseFloat(wdAmount) < minWd) { toast.error(`Minimum çıxarış ${minWd} ₼-dır`); return; }
    setBusy(true);
    try {
      await api.post("/withdrawals", { amount: parseFloat(wdAmount), details: wdDetails });
      toast.success("Çıxarış sorğusu göndərildi. Məbləğ balansdan tutuldu.");
      setWdAmount(""); setWdDetails("");
      await Promise.all([load(), refresh()]);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="font-heading text-3xl font-bold tracking-tight mb-6">Cüzdan</h1>

        <div className="rounded-3xl bg-primary text-primary-foreground p-8 mb-8 relative overflow-hidden">
          <div className="noise-overlay absolute inset-0 opacity-10" />
          <div className="relative">
            <p className="text-primary-foreground/70 text-sm flex items-center gap-2"><WalletIcon className="w-4 h-4" /> Mövcud balans</p>
            <p className="font-heading text-4xl sm:text-5xl font-bold mt-2" data-testid="wallet-balance">{azn(wallet?.balance)}</p>
            <div className="flex gap-6 mt-4 text-sm text-primary-foreground/70">
              <span>Sərmayə: <b className="text-primary-foreground">{azn(wallet?.total_invested)}</b></span>
              <span>Qazanc: <b className="text-primary-foreground">{azn(wallet?.total_earned)}</b></span>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Deposit */}
          <form onSubmit={submitDeposit} className="rounded-2xl border border-border bg-card p-6" data-testid="deposit-form">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center"><ArrowDownToLine className="w-4 h-4 text-primary" /></span>
              <h2 className="font-heading font-semibold">Balans artır</h2>
            </div>

            {info && (
              <div className="rounded-xl bg-primary text-primary-foreground p-4 mb-4 relative overflow-hidden" data-testid="deposit-card-box">
                <div className="noise-overlay absolute inset-0 opacity-10" />
                <div className="relative">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-primary-foreground/70 flex items-center gap-1.5"><CreditCard className="w-3.5 h-3.5" /> Ödəniş kartı</span>
                    <span className="text-xs text-primary-foreground/70">{info.bank_name}</span>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <p className="font-heading text-lg font-bold tracking-wider tabular-nums" data-testid="deposit-card-number">{info.card_number}</p>
                    <button type="button" onClick={copyCard} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors" data-testid="copy-card-btn" title="Kopyala">
                      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-sm text-primary-foreground/80 mt-1">{info.card_holder}</p>
                  {info.note && <p className="text-xs text-primary-foreground/60 mt-2">{info.note}</p>}
                </div>
              </div>
            )}

            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="dep">Məbləğ (₼)</Label>
                <Input id="dep" type="number" min={minDep} step="0.01" required value={depAmount} onChange={(e) => setDepAmount(e.target.value)} placeholder={`Minimum ${minDep}`} data-testid="deposit-amount-input" />
                <p className="text-xs text-muted-foreground">Minimum depozit: {minDep} ₼</p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="depnote">Qeyd (ödəniş üsulu / referans)</Label>
                <Textarea id="depnote" value={depNote} onChange={(e) => setDepNote(e.target.value)} placeholder="Bank köçürməsi referansı..." rows={2} data-testid="deposit-note-input" />
              </div>
              <Button type="submit" disabled={busy} className="w-full rounded-full" data-testid="deposit-submit-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Depozit sorğusu göndər"}
              </Button>
              <p className="text-xs text-muted-foreground flex items-start gap-1.5"><Landmark className="w-3.5 h-3.5 mt-0.5 shrink-0" /> Sorğunuz admin tərəfindən yoxlanıб balansınıza əlavə olunacaq.</p>
            </div>
          </form>

          {/* Withdraw */}
          <form onSubmit={submitWithdraw} className="rounded-2xl border border-border bg-card p-6" data-testid="withdraw-form">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-9 h-9 rounded-lg bg-accent/15 flex items-center justify-center"><ArrowUpFromLine className="w-4 h-4 text-accent" /></span>
              <h2 className="font-heading font-semibold">Pul çıxar</h2>
            </div>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="wd">Məbləğ (₼)</Label>
                <Input id="wd" type="number" min={minWd} step="0.01" required value={wdAmount} onChange={(e) => setWdAmount(e.target.value)} placeholder={`Minimum ${minWd}`} data-testid="withdraw-amount-input" />
                <p className="text-xs text-muted-foreground">Minimum çıxarış: {minWd} ₼ • Sorğu göndərildikdə məbləğ balansdan tutulur</p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="wddet">Kart nömrəsi</Label>
                <Textarea id="wddet" required value={wdDetails} onChange={(e) => setWdDetails(e.target.value)} placeholder="Vəsaitin köçürüləcəyi kart nömrəsi..." rows={2} data-testid="withdraw-details-input" />
              </div>
              <Button type="submit" disabled={busy} variant="outline" className="w-full rounded-full" data-testid="withdraw-submit-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Çıxarış sorğusu göndər"}
              </Button>
            </div>
          </form>
        </div>

        <Tabs defaultValue="tx">
          <TabsList data-testid="wallet-tabs">
            <TabsTrigger value="tx" data-testid="tab-tx">Əməliyyatlar</TabsTrigger>
            <TabsTrigger value="dep" data-testid="tab-dep">Depozitlər</TabsTrigger>
            <TabsTrigger value="wd" data-testid="tab-wd">Çıxarışlar</TabsTrigger>
          </TabsList>

          <TabsContent value="tx" className="mt-4">
            {txs.length === 0 ? <Empty text="Əməliyyat yoxdur" /> : (
              <div className="rounded-2xl border border-border bg-card divide-y divide-border" data-testid="tx-list">
                {txs.map((t) => (
                  <div key={t.tx_id} className="flex items-center justify-between px-5 py-3.5">
                    <div>
                      <p className="font-medium text-sm">{txLabel[t.type] || t.type}</p>
                      <p className="text-xs text-muted-foreground">{t.note} • {new Date(t.created_at).toLocaleString("az-AZ")}</p>
                    </div>
                    <span className={`font-heading font-semibold tabular-nums ${t.amount >= 0 ? "text-primary" : "text-destructive"}`}>
                      {t.amount >= 0 ? "+" : ""}{azn(t.amount)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="dep" className="mt-4">
            <RequestList items={deposits} amountKey="amount" testid="deposit-list" emptyText="Depozit sorğusu yoxdur" />
          </TabsContent>
          <TabsContent value="wd" className="mt-4">
            <RequestList items={withdrawals} amountKey="amount" testid="withdrawal-list" emptyText="Çıxarış sorğusu yoxdur" />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function Empty({ text }) {
  return <div className="rounded-2xl border border-dashed border-border p-10 text-center text-muted-foreground">{text}</div>;
}

function RequestList({ items, testid, emptyText }) {
  if (items.length === 0) return <Empty text={emptyText} />;
  return (
    <div className="rounded-2xl border border-border bg-card divide-y divide-border" data-testid={testid}>
      {items.map((r) => {
        const st = statusMap[r.status] || { label: r.status, variant: "secondary" };
        return (
          <div key={r.req_id} className="flex items-center justify-between px-5 py-3.5">
            <div>
              <p className="font-heading font-semibold">{azn(r.amount)}</p>
              <p className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString("az-AZ")}</p>
            </div>
            <Badge variant={st.variant}>{st.label}</Badge>
          </div>
        );
      })}
    </div>
  );
}
